import React, { useState, useContext, useEffect } from "react";
import { host } from "../../config";
import { AuthContext } from "../authProvider/authProvider";
import "./implementationStatus.css";
import {
  Box,
  Button,
  CircularProgress,
  Card,
  TextField,
  CardContent,
  Typography,
  Modal,
  Alert,
  InputLabel,
} from "@mui/material";
import Grid from '@mui/material/GridLegacy';
import Tooltip from "@mui/material/Tooltip";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CustomAlert from "components/generals/alert/CustomAlert";
import { useNavigate } from "react-router-dom";

interface git_information {
  [key: string]: boolean | string;
  submitters_email_gif: string;
  sent_docusign_docs: boolean;
  documents_signed: boolean;
  enrollments_verified: boolean;
  cards_released_for_mail: boolean;
  ach_form: boolean;
  first_bill_sent: boolean;
  underwriting_review_complete: boolean;
  issue_policy_and_certs: boolean;
  training_provided_on_optiadmin: boolean;
}

interface colors {
  green_bool: boolean;
  yellow_bool: boolean;
  red_bool: boolean;
}

interface statusObj {
  [key: string]: string | boolean | number | git_information | colors;
  group_name: string;
  git_information: git_information;
  colors: colors;
}

interface CardProps {
  title: string;
  content: string;
}

const ImplementationStatus: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [groupNo, setGroupNo] = useState<string | null>(null);
  const [statusObj, setStatusObj] = useState<statusObj>({
    group_name: "",
    git_information: {
      submitters_email_gif: "",
      sent_docusign_docs: false,
      documents_signed: false,
      enrollments_verified: false,
      cards_released_for_mail: false,
      ach_form: false,
      first_bill_sent: false,
      underwriting_review_complete: false,
      issue_policy_and_certs: false,
      training_provided_on_optiadmin: false,
    },
    colors: {
      green_bool: false,
      yellow_bool: false,
      red_bool: false,
    },
  });
  const [showCard, setShowCard] = useState<boolean>(false);
  const [cardColor, setCardColor] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [errorAlert, setErrorAlert] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>("");
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");

  const openAlert = () => {
    setAlertOpen(true);
  };

  const closeAlert = () => {
    setAlertOpen(false);
  };
  useEffect(() => {
    console.log("Effect ran due to change in isAlertOpen or alertMessage", isAlertOpen, alertMessage);
    // rest of your effect logic
  }, [isAlertOpen, alertMessage]);

  const navigate = useNavigate();

  const field_names = {
    sent_docusign_docs: "Plan Contracts and Agreements sent electronically to Broker and/or group",
    ach_form: "Payment provided by group",
    enrollments_verified: "Enrolments have been supplied, verified and processed",
    cards_released_for_mail: "Quality check on cards completed and cards have been mailed",
    underwriting_review_complete: "Underwriting review has been completed",
    submitters_email_gif: "Group Set up details provided by group/ broker and processed.",
    first_bill_sent: "Initial invoice generated and provided to group",
    documents_signed: "Contracting Documents Signed",
    issue_policy_and_certs: "Policy and Certificate uploaded",
    training_provided_on_optiadmin: "Training provided to employer on OptiAdmin",
  };

  const tooltips = {
    submitters_email_gif:
      "Please note if you submitted all information and this is not showing as complete, please reach out to us",
    cards_released_for_mail:
      "Cards have not completed the quality checks and are not released for creation and mailing",
    first_bill_sent: "We have not generated the groups initial invoice.",
    underwriting_review_complete: "We have not completed the underwriting for this group",
  };

  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error("AuthContext is undefined");
  }

  const handleGroupNumberChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setGroupNo(event.target.value);
  };

  const goToDocumentUploads = () => {
    navigate("/upload");
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setErrorAlert(false);
    setErrorText("");
    if (!groupNo) {
      alert("Please enter a group number");
    } else {
      let params = new URLSearchParams();
      if (groupNo != null) params.append("groupNo", groupNo);

      console.log(params);
      setIsLoading(true);

      fetch(`${host}/api/implementation/groupStatus?` + params, {
        method: "GET",
        headers: {},
      })
        .then(async (response: Response) => {
          setIsLoading(false);
          if (response.status != 200) {
            setErrorAlert(true);
            setErrorText(await response.text());
            setAlertMessage("Invalid Input");
            setAlertOpen(true);
          } else {
            response
              .json()
              .then((res) => {
                setStatusObj(res);
                console.log(typeof res.git_information["submitters_email_gif"]);
                if (res.colors.green_bool == true) {
                  setCardColor("green");
                  handleOpen();
                } else if (res.colors.red_bool == true) setCardColor("red");
                else if (res.colors.yellow_bool == true) setCardColor("yellow");
                setIsLoading(false);
              })
              .then(() => {
                setShowCard(true);
              });
          }
        })
        .catch((error: any) => {
          console.log(error);
          setIsLoading(false);
          setAlertMessage("Invalid group number");
          setAlertOpen(true);
        })
        .finally(() => {
          // Code to execute in the finally block
          // This will run regardless of whether an error occurred or not
          setIsLoading(false);
        });
    }
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }

  return (
    <div>
      <form className="implementation-form" onSubmit={handleSubmit}>
        <h2 className="centered-title">OptiMed Health Group Implementation Status</h2>
        <p className="centered-text">
          Please enter the group number in order for us to provide an update for you on the group’s implementation. If
          you do not have a group number please contact your account manager or sales resource.
        </p>
        <TextField
          type="text"
          name="groupNumber"
          value={groupNo}
          onChange={handleGroupNumberChange}
          placeholder="Group Number"
        />
        {errorAlert && <Alert severity="error">{errorText}</Alert>}
        <Button type="submit" className="blue-button" style={{ backgroundColor: "#004e9d", color: "#ffffff" }}>
          Submit
        </Button>
      </form>
      <CustomAlert message={alertMessage} isOpen={isAlertOpen} onClose={closeAlert} />
      <div className="status-section">
        {showCard && (
          <div className="status-cards-container">
            <Box className="status-box" component="span">
              <h2>{statusObj.group_name}</h2>
              <p className="smaller-text">
                {cardColor === "green" ? (
                  <div>
                    <Modal
                      open={open}
                      onClose={handleClose}
                      aria-labelledby="modal-modal-title"
                      aria-describedby="modal-modal-description"
                    >
                      <Box
                        sx={{
                          position: "absolute" as "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: 400,
                          bgcolor: "background.paper",
                          border: "1px solid #ddd",
                          boxShadow: 24,
                          p: 4,
                        }}
                      >
                        <Typography id="modal-modal-title" variant="h6" component="h2">
                          Congratulations!
                        </Typography>
                        <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                          This group has been successfully set up!
                        </Typography>
                      </Box>
                    </Modal>
                  </div>
                ) : (
                  "We are processing your group. Please note that the tasks below with a green outline have been completed. We have also provided our Document Upload link at the bottom of the page should you need to send us any documents."
                )}
              </p>
              <Box
                component="span"
                sx={{
                  border:
                    cardColor == "green"
                      ? "4px solid green"
                      : cardColor == "yellow"
                      ? "4px solid yellow"
                      : "4px solid red",
                  backgroundColor: cardColor == "green" ? "green" : cardColor == "yellow" ? "yellow" : "red",
                  flex: "100%",
                  display: "flex",
                  borderRadius: "6px",
                }}
              ></Box>
            </Box>
            <Grid container spacing={2}>
              {Object.keys(statusObj.git_information).map((key, index) => (
                <Grid item xs={6} sm={12} md={12} key={index}>
                  {statusObj.git_information[key] == true || typeof statusObj.git_information[key] === "string" ? (
                    <Card
                      className="status-card"
                      variant="outlined"
                      sx={{
                        minWidth: 275,
                        border:
                          statusObj.git_information[key] == true || typeof statusObj.git_information[key] === "string"
                            ? "4px solid green"
                            : "4px solid red",
                      }}
                    >
                      <CardContent>
                        <Grid container>
                          <Grid item xs={6} sm={10} md={10}>
                            <Typography className="status-card-content" sx={{ fontSize: 14 }} color="text.secondary">
                              {field_names[key as keyof typeof field_names]}
                            </Typography>
                          </Grid>
                          <Grid item xs={2} sm={2} md={2}>
                            {statusObj.git_information[key] == true ||
                            typeof statusObj.git_information[key] === "string" ? (
                              <div>
                                <CheckIcon style={{ float: "right", color: "green" }} />
                              </div>
                            ) : (
                              <div>
                                <CloseIcon style={{ float: "right", color: "red" }} />
                              </div>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card
                      className="missing-status-card"
                      variant="outlined"
                      sx={{
                        minWidth: 275,
                        border: statusObj.git_information[key] ? "4px solid green" : "4px solid red",
                      }}
                    >
                      <Tooltip title={tooltips[key as keyof typeof tooltips]}>
                        <CardContent>
                          <Grid container>
                            <Grid item xs={6} sm={10} md={10}>
                              <Typography className="status-card-content" sx={{ fontSize: 14 }} color="text.secondary">
                                {field_names[key as keyof typeof field_names]}
                              </Typography>
                            </Grid>
                            <Grid item xs={2} sm={2} md={2}>
                              {statusObj.git_information[key] ? (
                                <div>
                                  <CheckIcon style={{ float: "right", color: "green" }} />
                                </div>
                              ) : (
                                <div>
                                  <CloseIcon style={{ float: "right", color: "red" }} />
                                </div>
                              )}
                            </Grid>
                          </Grid>

                          {/* Conditionally render Tooltip */}
                        </CardContent>
                      </Tooltip>
                    </Card>
                  )}
                </Grid>
              ))}
              <Button
                className="navigateButton"
                type="button"
                variant="contained"
                onClick={goToDocumentUploads}
                sx={{
                  margin: "12px",
                  backgroundColor: "#004e9d",
                  color: "#ffffff",
                }}
              >
                Document Uploads
              </Button>
            </Grid>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImplementationStatus;
// ... Existing code
export {};
